import { enqueueSnackbar } from "notistack";
import { useReducer } from "react";
import Text from "../../../../comp/tw/Text";
import { TextFieldTWSolo } from "../../../../comp/tw/TextField";
import { ButtonTW } from "../../../../comp/tw/Button";
import ImageIcon from "@mui/icons-material/Image";
import { api } from "../../../../util/api";
import camelToTitle from "../../../../util/case";

const typesOfMaterials = [
  "mesh",
  "mainPost",
  "mainPostCap",
  "linePost",
  "linePostCap",
  "topRail", // length
  "topRailEndConnector",
  "topRailSleeve",
  "centerMainPostBand",
  "nutAndBolt", // pack
  "tensionBand",
  "tensionBar",
  "concrete",
  "fenceTies", // pack
  "tensionWire",
  "barbedWire",
];

const gateStuff = [
  "gate",
  "singleGateKit",
  "caneBolt",
  "doubleGateKit",
  "gateExtension",
];

const decimalCleanUp = (target) => {
  target = target.replace(/[^0-9.]/g, "");

  // Ensure that there's only one decimal point
  const decimalCount = (target.match(/\./g) || []).length;
  if (decimalCount > 1) {
    target = target.slice(0, target.lastIndexOf("."));
  }
  return target;
};

const MaterialInput = ({ materialKey, material, dispatch }) => {
  const handleMaterialChange = (field, value) => {
    dispatch({
      type: "updateMaterial",
      payload: {
        key: materialKey,
        field,
        value: field === "name" ? value : decimalCleanUp(value),
      },
    });
  };

  const toggleIncluded = () => {
    dispatch({
      type: "toggleMaterial",
      payload: { key: materialKey },
    });
  };

  return (
    <div
      className={`border-solid border-zinc-200 rounded border-2 p-3 mb-2 ${!material.included ? "bg-gray-100" : ""}`}
    >
      <div className="flex justify-between items-center mb-2">
        <h4
          className={`text-lg font-semibold ${!material.included ? "text-gray-500" : ""}`}
        >
          {camelToTitle(material.category)}
        </h4>
        <ButtonTW
          label={material.included ? "X" : "+"}
          variant={material.included ? "danger" : "primary"}
          onClick={toggleIncluded}
        />
      </div>
      {material.included && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <TextFieldTWSolo
            label="Material Name"
            value={material.name}
            onChange={(e) => handleMaterialChange("name", e.target.value)}
            placeholder="e.g., 6ft Chain Link Mesh"
          />
          {materialKey === "mesh" ? (
            <>
              <TextFieldTWSolo
                label="Roll Length (ft)"
                value={material.rollLength}
                onChange={(e) =>
                  handleMaterialChange("rollLength", e.target.value)
                }
                placeholder="e.g., 50"
              />
              <TextFieldTWSolo
                label="Roll Cost ($)"
                value={material.rollCost}
                onChange={(e) =>
                  handleMaterialChange("rollCost", e.target.value)
                }
                placeholder="e.g., 175.00"
              />
            </>
          ) : (
            <TextFieldTWSolo
              label="Price ($)"
              value={material.price}
              onChange={(e) => handleMaterialChange("price", e.target.value)}
              placeholder="e.g., 45.99"
            />
          )}
        </div>
      )}
    </div>
  );
};

const GateInput = ({ materialKey, material, dispatch }) => {
  const handleMaterialChange = (field, value) => {
    dispatch({
      type: "updateGate",
      payload: {
        key: materialKey,
        field,
        value: field === "name" ? value : decimalCleanUp(value),
      },
    });
  };

  const toggleIncluded = () => {
    dispatch({
      type: "toggleGate",
      payload: { key: materialKey },
    });
  };

  return (
    <div
      className={`border-solid border-zinc-200 rounded border-2 p-3 mb-2 ${!material.included ? "bg-gray-100" : ""}`}
    >
      <div className="flex justify-between items-center mb-2">
        <h4
          className={`text-lg font-semibold ${!material.included ? "text-gray-500" : ""}`}
        >
          {camelToTitle(material.category)}
        </h4>
        <ButtonTW
          label={material.included ? "X" : "+"}
          variant={material.included ? "danger" : "primary"}
          onClick={toggleIncluded}
        />
      </div>
      {material.included && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <TextFieldTWSolo
            label="Material Name"
            value={material.name}
            onChange={(e) => handleMaterialChange("name", e.target.value)}
            placeholder="e.g., 4ft Walk Gate"
          />
          <TextFieldTWSolo
            label="Price ($)"
            value={material.price}
            onChange={(e) => handleMaterialChange("price", e.target.value)}
            placeholder="e.g., 45.99"
          />
        </div>
      )}
    </div>
  );
};

const Build = ({ designPageState, dispatch }) => {
  const handleFieldChange = (field, value) => {
    dispatch({
      type: "updateField",
      payload: { field, value: decimalCleanUp(value) },
    });
  };

  const handleImageChange = (e) => {
    enqueueSnackbar("Processing Design Image");

    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();
      img.onload = async () => {
        const aspectRatio = img.width / img.height;
        const maxWidth = 2000;
        const maxHeight = 2000;

        let targetWidth;
        let targetHeight;

        if (img.width > maxWidth || img.height > maxHeight) {
          if (img.width > img.height) {
            targetWidth = maxWidth;
            targetHeight = maxWidth / aspectRatio;
          } else {
            targetHeight = maxHeight;
            targetWidth = maxHeight * aspectRatio;
          }
        } else {
          targetWidth = img.width;
          targetHeight = img.height;
        }

        createImageBitmap(img, {
          resizeWidth: targetWidth,
          resizeHeight: targetHeight,
        }).then(async (resizedBitmap) => {
          let quality = 0.99;
          let canvas = document.createElement("canvas");
          canvas.width = targetWidth;
          canvas.height = targetHeight;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(resizedBitmap, 0, 0, targetWidth, targetHeight);
          let dataUrl = canvas.toDataURL("image/jpeg", quality);

          while (dataUrl.length > 1000000) {
            quality -= 0.01;
            dataUrl = canvas.toDataURL("image/jpeg", quality);
          }

          dispatch({ type: "updateImage", payload: dataUrl });
          enqueueSnackbar("Design Image Added", { variant: "success" });
        });
      };

      img.src = event.target.result;
    };

    reader.readAsDataURL(file);
  };

  const deleteImage = () => {
    dispatch({ type: "updateImage", payload: "" });
    enqueueSnackbar("Image Removed", { variant: "success" });
  };

  const handleSave = async () => {
    // Validate design name
    if (!designPageState.name || designPageState.name.trim() === "") {
      enqueueSnackbar("Please enter a design name", { variant: "error" });
      return;
    }

    // Validate configuration fields
    if (!designPageState.postSpacing) {
      enqueueSnackbar("Please enter post spacing", { variant: "error" });
      return;
    }

    if (!designPageState.topRailLength) {
      enqueueSnackbar("Please enter top rail length", { variant: "error" });
      return;
    }

    if (!designPageState.concretePerLinePost) {
      enqueueSnackbar("Please enter concrete per line post", {
        variant: "error",
      });
      return;
    }

    if (!designPageState.concretePerMainPost) {
      enqueueSnackbar("Please enter concrete per main post", {
        variant: "error",
      });
      return;
    }

    if (!designPageState.tensionBandsPerMainPost) {
      enqueueSnackbar("Please enter tension bands per main post", {
        variant: "error",
      });
      return;
    }

    if (!designPageState.maxLengthBetweenMainPosts) {
      enqueueSnackbar("Please enter max length between main posts", {
        variant: "error",
      });
      return;
    }

    if (!designPageState.fenceTiesEveryFt) {
      enqueueSnackbar("Please enter fence ties every ft", {
        variant: "error",
      });
      return;
    }

    if (!designPageState.tensionWireLength) {
      enqueueSnackbar("Please enter tension wire length", {
        variant: "error",
      });
      return;
    }

    if (!designPageState.meshLength) {
      enqueueSnackbar("Please enter mesh length", { variant: "error" });
      return;
    }

    // Check if at least one material is included and has required fields
    const includedMaterials = Object.values(designPageState.materials).filter(
      (mat) => mat.included,
    );

    if (includedMaterials.length === 0) {
      enqueueSnackbar("Please include at least one material", {
        variant: "error",
      });
      return;
    }

    // Validate included materials have name and price
    for (const material of includedMaterials) {
      if (!material.name || material.name.trim() === "") {
        enqueueSnackbar(
          `Please enter a name for ${camelToTitle(material.category)}`,
          { variant: "error" },
        );
        return;
      }
      if (material.category === "mesh") {
        if (!material.rollLength) {
          enqueueSnackbar("Please enter a roll length for Mesh", {
            variant: "error",
          });
          return;
        }
        if (!material.rollCost) {
          enqueueSnackbar("Please enter a roll cost for Mesh", {
            variant: "error",
          });
          return;
        }
      } else if (!material.price) {
        enqueueSnackbar(
          `Please enter a price for ${camelToTitle(material.category)}`,
          { variant: "error" },
        );
        return;
      }
    }

    // Validate included gate materials
    const includedGateMaterials = Object.values(designPageState.gate).filter(
      (mat) => mat.included,
    );

    for (const material of includedGateMaterials) {
      if (!material.name || material.name.trim() === "") {
        enqueueSnackbar(
          `Please enter a name for ${camelToTitle(material.category)}`,
          { variant: "error" },
        );
        return;
      }
      if (!material.price) {
        enqueueSnackbar(
          `Please enter a price for ${camelToTitle(material.category)}`,
          { variant: "error" },
        );
        return;
      }
    }

    enqueueSnackbar("Design saving successfully");

    const meshMaterial = designPageState.materials.mesh;
    const stateToSave = {
      ...designPageState,
      materials: {
        ...designPageState.materials,
        mesh: {
          ...meshMaterial,
          price: meshMaterial.rollCost,
        },
      },
    };

    const response = await api("POST", `/team/design/chainlink`, stateToSave);
    console.log("API Response:", response);
    enqueueSnackbar("Design saved successfully", { variant: "success" });

    console.log("Design State:", designPageState);
  };

  return (
    <div className="space-y-6">
      <div className="border-solid border-zinc-300 rounded-lg border-4 p-4">
        <h3 className="text-2xl font-semibold mb-4">
          Chain Link Design Configuration
        </h3>

        <div className="space-y-4">
          <TextFieldTWSolo
            label="Design Name"
            value={designPageState.name}
            onChange={(e) =>
              dispatch({ type: "updateDesignName", payload: e.target.value })
            }
            placeholder="e.g., 4ft Residential Chain Link"
          />

          {/* Image Upload Section */}
          <div className="border-2 border-gray-300 rounded-lg p-4">
            <h4 className="text-lg font-semibold mb-2">Design Image</h4>
            {designPageState.image ? (
              <div className="flex flex-col items-center gap-4">
                <img
                  alt="design"
                  src={designPageState.image}
                  className="max-h-48 rounded"
                />
                <div className="flex gap-2">
                  <div className="transform hover:scale-105 hover:shadow-2xl px-3 py-2 inline-flex items-center justify-center font-semibold transition ease-in-out duration-200 rounded focus:outline-none focus:ring-4 text-white bg-gradient-to-r from-indigo-500 via-blue-500 to-indigo-500 hover:from-indigo-500 hover:via-purple-500 hover:to-indigo-500 shadow-lg shadow-indigo-500/50 focus:ring-indigo-400 cursor-pointer">
                    <label
                      htmlFor="chainlinkImageUpload"
                      className="cursor-pointer"
                    >
                      <ImageIcon /> Change Image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      id="chainlinkImageUpload"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </div>
                  <ButtonTW
                    variant="danger"
                    label="Remove Image"
                    onClick={deleteImage}
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 py-4">
                <p className="text-gray-500 mb-2">No Image</p>
                <div className="transform hover:scale-105 hover:shadow-2xl px-3 py-2 inline-flex items-center justify-center font-semibold transition ease-in-out duration-200 rounded focus:outline-none focus:ring-4 text-white bg-gradient-to-r from-indigo-500 via-blue-500 to-indigo-500 hover:from-indigo-500 hover:via-purple-500 hover:to-indigo-500 shadow-lg shadow-indigo-500/50 focus:ring-indigo-400 cursor-pointer">
                  <label
                    htmlFor="chainlinkImageUpload"
                    className="cursor-pointer"
                  >
                    <ImageIcon /> Upload Image
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    id="chainlinkImageUpload"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextFieldTWSolo
              label="Post Spacing (ft)"
              value={designPageState.postSpacing}
              onChange={(e) => handleFieldChange("postSpacing", e.target.value)}
              placeholder="e.g., 10"
            />

            <TextFieldTWSolo
              label="Top Rail Length (ft)"
              value={designPageState.topRailLength}
              onChange={(e) =>
                handleFieldChange("topRailLength", e.target.value)
              }
              placeholder="e.g., 10"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextFieldTWSolo
              label="Concrete Per Line Post (bags)"
              value={designPageState.concretePerLinePost}
              onChange={(e) =>
                handleFieldChange("concretePerLinePost", e.target.value)
              }
              placeholder="e.g., 1"
            />

            <TextFieldTWSolo
              label="Concrete Per Main Post (bags)"
              value={designPageState.concretePerMainPost}
              onChange={(e) =>
                handleFieldChange("concretePerMainPost", e.target.value)
              }
              placeholder="e.g., 2"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextFieldTWSolo
              label="Tension Bands Per Main Post"
              value={designPageState.tensionBandsPerMainPost}
              onChange={(e) =>
                handleFieldChange("tensionBandsPerMainPost", e.target.value)
              }
              placeholder="e.g., 3"
            />

            <TextFieldTWSolo
              label="Max Length Between Main Posts (ft)"
              value={designPageState.maxLengthBetweenMainPosts}
              onChange={(e) =>
                handleFieldChange("maxLengthBetweenMainPosts", e.target.value)
              }
              placeholder="e.g., 100"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextFieldTWSolo
              label="Fence Ties Every Ft"
              value={designPageState.fenceTiesEveryFt}
              onChange={(e) =>
                handleFieldChange("fenceTiesEveryFt", e.target.value)
              }
              placeholder="e.g., 2"
            />

            <TextFieldTWSolo
              label="Tension Wire Length (ft)"
              value={designPageState.tensionWireLength}
              onChange={(e) =>
                handleFieldChange("tensionWireLength", e.target.value)
              }
              placeholder="e.g., 100"
            />
          </div>

          <TextFieldTWSolo
            label="Length of the Mesh (ft)"
            value={designPageState.meshLength}
            onChange={(e) => handleFieldChange("meshLength", e.target.value)}
            placeholder="e.g., 50"
          />
        </div>
      </div>

      <div className="border-solid border-zinc-300 rounded-lg border-4 p-4">
        <h3 className="text-2xl font-semibold mb-4">Fence Materials</h3>
        {typesOfMaterials.map((materialKey) => (
          <MaterialInput
            key={materialKey}
            materialKey={materialKey}
            material={designPageState.materials[materialKey]}
            dispatch={dispatch}
          />
        ))}
      </div>

      <div className="border-solid border-zinc-300 rounded-lg border-4 p-4">
        <h3 className="text-2xl font-semibold mb-4">Gate Materials</h3>
        {gateStuff.map((materialKey) => (
          <GateInput
            key={materialKey}
            materialKey={materialKey}
            material={designPageState.gate[materialKey]}
            dispatch={dispatch}
          />
        ))}
      </div>

      <div className="flex justify-end gap-4">
        <ButtonTW
          label="Reset"
          variant="secondary"
          onClick={() => dispatch({ type: "resetDesign" })}
        />
        <ButtonTW label="Save Design" variant="primary" onClick={handleSave} />
      </div>
    </div>
  );
};

export default Build;
